/**
 * **参考光栅器**（架构文档 §4.2 的 ③ 的一个实现）—— 把 IR 画成 **1-bit 位图**。
 *
 * ## 它为什么值得存在
 *
 * 热敏机只有"上墨 / 不上墨"，所以"打印出来是什么样"这个问题有一个
 * **完全确定的答案**：一个 0/1 矩阵。有了它：
 *
 *   · **不碰打印机也能判决**（§12.2：架构是否成立在 S5 就能验，不需要真机）；
 *   · 几何类的不变量变成可断言的：静区里一个墨点都没有、圆角外面是空的、
 *     模块与矩阵**逐点**对应、什么都没画到纸外；
 *   · C++ 那边（Skia）的实现有一个**可对照的规格**：先对齐这个矩阵，
 *     再谈抗锯齿之类的像素外观差异。
 *
 * ## 它的边界（写清楚，别当成"已经和 Skia 一致了"）
 *
 * * **字形不在这里**：IR 冻的是"这一行占多宽 / 基线在哪"，字形像素由各端字体
 *   决定（§4.2 的 ③）。所以本模块对文本只画**槽位**（可选：把文本占位框画成
 *   空心矩形，方便看排版对不对），并如实在 `Raster.stats.textSlots` 里报出来。
 * * **图片**同理：解码是宿主的事（`decodeImage` 钩子），没给钩子就只画占位框。
 * * **任意角度的旋转**用最近邻（几何在 IR 里已吸附整数点，90° 的倍数完全精确）。
 *
 * ## 为什么不是 canvas
 *
 * 无头环境里拿不到 canvas 上下文（那块就永远测不到）；而且这里要的**不是**
 * 好看的画面，是**逐点可断言的矩阵**。纯算术实现能做到"跑在哪都一样"。
 */
import type { PrintDrawOp, PageRenderTree, PrintClip } from './renderTree'

/** 一张 1-bit 位图（行优先；`1` = 上墨） */
export interface Raster {
  widthDots: number
  heightDots: number
  /** 长度 = `width × height`，`Uint8Array`（1 = 墨） */
  bits: Uint8Array
  stats: {
    /** 上墨点数 */
    ink: number
    /** 文本槽位数（文本的**像素**不在这里画 —— 见文件头） */
    textSlots: number
    /** 因为图片没有解码器而只画了框的个数 */
    imagePlaceholders: number
  }
}

export interface RasterOptions {
  /**
   * 图片解码钩子：给 IR 里的 `src` 返回一张 1-bit 位图（宽高自定，会按 `fit` 缩放）。
   *
   * ⚠️ 不提供时**不报错**：只画一条占位框并把数字记进 `stats` ——
   * "取不到图"是正常处境（离线环境、资产还没下载完）。
   */
  decodeImage?: (src: string) => { widthDots: number; heightDots: number; bits: Uint8Array } | null
  /** 是否把文本的占位框画成空心矩形（排错用；默认 false = 完全不画文本像素） */
  outlineText?: boolean
}

const at = (raster: Raster, x: number, y: number): boolean =>
  x >= 0 && y >= 0 && x < raster.widthDots && y < raster.heightDots

function setInk(raster: Raster, x: number, y: number): void {
  if (!at(raster, x, y)) return
  const index = y * raster.widthDots + x
  if (raster.bits[index] === 1) return
  raster.bits[index] = 1
  raster.stats.ink += 1
}

/** 读一个点（不在纸上 = 没墨） */
export function inkAt(raster: Raster, x: number, y: number): boolean {
  if (!at(raster, x, y)) return false
  return raster.bits[y * raster.widthDots + x] === 1
}

/**
 * 点是否落在圆角矩形内。
 *
 * 圆角 = 四个角各切一个半径 `r` 的圆：判断点是否落在"角上那个圆心"的圆内。
 * ⚠️ 这是**像素级**的圆角（不是抗锯齿的）—— 1-bit 输出本来就只有"在 / 不在"。
 */
export function insideRoundedRect(
  px: number,
  py: number,
  rect: { xDots: number; yDots: number; wDots: number; hDots: number; radiusDots: number },
): boolean {
  const { xDots: x, yDots: y, wDots: w, hDots: h } = rect
  if (px < x || py < y || px >= x + w || py >= y + h) return false
  const r = Math.max(0, Math.min(rect.radiusDots, Math.floor(Math.min(w, h) / 2)))
  if (r <= 0) return true
  /* 落在四个角的"方形禁区"里才需要按圆判 */
  const cx = px < x + r ? x + r : px >= x + w - r ? x + w - r - 1 : px
  const cy = py < y + r ? y + r : py >= y + h - r ? y + h - r - 1 : py
  const dx = px - cx
  const dy = py - cy
  return dx * dx + dy * dy <= r * r
}

/** 把 op 的旋转（绕枢轴，顺时针）作用到一个点上 —— 只用于"把目标点转回源坐标" */
function unrotate(
  px: number,
  py: number,
  rotation: { rotationDeg: number; pivotXDots: number; pivotYDots: number } | undefined,
): { x: number; y: number } {
  if (!rotation || rotation.rotationDeg === 0) return { x: px, y: py }
  const rad = (-rotation.rotationDeg * Math.PI) / 180
  const dx = px - rotation.pivotXDots
  const dy = py - rotation.pivotYDots
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return {
    x: Math.round(rotation.pivotXDots + dx * cos - dy * sin),
    y: Math.round(rotation.pivotYDots + dx * sin + dy * cos),
  }
}

/** 源框绕枢轴转 `rotationDeg` 之后的**外接盒**（只用于决定遍历范围） */
function rotatedBounds(
  box: { xDots: number; yDots: number; wDots: number; hDots: number },
  rotation: { rotationDeg: number; pivotXDots: number; pivotYDots: number },
): { xDots: number; yDots: number; wDots: number; hDots: number } {
  const rad = (rotation.rotationDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const corners = [
    { x: box.xDots, y: box.yDots },
    { x: box.xDots + box.wDots, y: box.yDots },
    { x: box.xDots, y: box.yDots + box.hDots },
    { x: box.xDots + box.wDots, y: box.yDots + box.hDots },
  ].map((point) => {
    const dx = point.x - rotation.pivotXDots
    const dy = point.y - rotation.pivotYDots
    return {
      x: rotation.pivotXDots + dx * cos - dy * sin,
      y: rotation.pivotYDots + dx * sin + dy * cos,
    }
  })
  const xs = corners.map((point) => point.x)
  const ys = corners.map((point) => point.y)
  const minX = Math.floor(Math.min(...xs))
  const minY = Math.floor(Math.min(...ys))
  return {
    xDots: minX,
    yDots: minY,
    wDots: Math.ceil(Math.max(...xs)) - minX + 1,
    hDots: Math.ceil(Math.max(...ys)) - minY + 1,
  }
}

/** 一条 op 画到纸上（内部入口：先把"目标点"反旋回 op 自己的坐标） */
function rasterizeOp(raster: Raster, op: PrintDrawOp, options: RasterOptions): void {
  const rotation = op.rotation
  const clip = op.clip ?? null

  /**
   * 逐点遍历一个包围盒，把点反旋回 op 坐标后问"这个点该不该上墨"。
   *
   * ⚠️ **反着转**（从目标点回源点）而不是把图转过去：这样"哪里该有墨"
   * 永远在 op 自己的坐标系里判断，不用担心旋转后的空隙（正向旋转 + 最近邻
   * 会留下没被访问到的目标点）。
   */
  const paint = (
    box: { xDots: number; yDots: number; wDots: number; hDots: number },
    hit: (sx: number, sy: number) => boolean,
  ): void => {
    /*
     * ⚠️ 迭代的范围是**旋转之后的外接盒**，不是源框：
     * 转 90° 之后墨会落到源框**外面**，只遍历源框的话等于"什么都没转"
     * （第一版就是这样：旋转那条断言拿到的是原样的包围盒）。
     */
    const area = rotation ? rotatedBounds(box, rotation) : box
    for (let py = area.yDots; py < area.yDots + area.hDots; py += 1) {
      for (let px = area.xDots; px < area.xDots + area.wDots; px += 1) {
        const source = unrotate(px, py, rotation)
        if (clip && !insideRoundedRect(source.x, source.y, clip)) continue
        if (hit(source.x, source.y)) setInk(raster, px, py)
      }
    }
  }

  if (op.op === 'rect') {
    paint({ xDots: op.xDots, yDots: op.yDots, wDots: op.wDots, hDots: op.hDots }, (sx, sy) => {
      if (op.radiusDots > 0 && !insideRoundedRect(sx, sy, {
        xDots: op.xDots,
        yDots: op.yDots,
        wDots: op.wDots,
        hDots: op.hDots,
        radiusDots: op.radiusDots,
      })) {
        return false
      }
      const dx = sx - op.xDots
      const dy = sy - op.yDots
      if (op.invert) return true
      if (op.fill) return true
      if (op.lineWidthDots <= 0) return false
      return (
        dx < op.lineWidthDots ||
        dy < op.lineWidthDots ||
        dx >= op.wDots - op.lineWidthDots ||
        dy >= op.hDots - op.lineWidthDots
      )
    })
    return
  }

  if (op.op === 'barcode' && op.kind === '1d') {
    const box = {
      xDots: op.bars.length > 0 ? op.bars[0]!.xDots : op.xDots,
      yDots: op.yDots,
      wDots: op.bars.reduce((max, bar) => Math.max(max, bar.xDots + bar.wDots), op.xDots) -
        (op.bars.length > 0 ? op.bars[0]!.xDots : op.xDots),
      hDots: op.heightDots,
    }
    paint(box, (sx, sy) => {
      /*
       * ⚠️ 逐条判断：**条的边界是整数点**（IR 已经吸过），所以这里的命中
       * 是"逐点精确"的 —— 这正是"扫码率"依赖的那件事。
       */
      for (const bar of op.bars) {
        if (sx >= bar.xDots && sx < bar.xDots + bar.wDots) return true
      }
      return false
    })
    return
  }

  if (op.op === 'barcode' && op.kind === '2d') {
    paint(
      {
        xDots: op.xDots,
        yDots: op.yDots,
        wDots: op.cols * op.moduleDots,
        hDots: op.rows * op.moduleDots,
      },
      (sx, sy) => {
        const col = Math.floor((sx - op.xDots) / op.moduleDots)
        const row = Math.floor((sy - op.yDots) / op.moduleDots)
        if (col < 0 || row < 0 || col >= op.cols || row >= op.rows) return false
        const line = op.matrix[row] ?? ''
        return line[col] === '1'
      },
    )
    return
  }

  if (op.op === 'image') {
    const decoded = options.decodeImage?.(op.src) ?? null
    if (!decoded) {
      raster.stats.imagePlaceholders += 1
      /* 占位：一条空心框（和编辑器里"还没有图像源"的观感一致） */
      paint({ xDots: op.xDots, yDots: op.yDots, wDots: op.wDots, hDots: op.hDots }, (sx, sy) => {
        const dx = sx - op.xDots
        const dy = sy - op.yDots
        return dx === 0 || dy === 0 || dx === op.wDots - 1 || dy === op.hDots - 1
      })
      return
    }
    paint({ xDots: op.xDots, yDots: op.yDots, wDots: op.wDots, hDots: op.hDots }, (sx, sy) => {
      /* `fit`：把目标的点映射回源图（`contain` / `cover` 保比例，`fill` 直接拉） */
      const scaleX = decoded.widthDots / op.wDots
      const scaleY = decoded.heightDots / op.hDots
      let kx = scaleX
      let ky = scaleY
      if (op.fit !== 'fill') {
        const k = op.fit === 'cover' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY)
        kx = k
        ky = k
      }
      const offsetX =
        op.fit === 'cover' || op.fit === 'contain'
          ? (op.wDots - decoded.widthDots / kx) / 2
          : 0
      const offsetY =
        op.fit === 'cover' || op.fit === 'contain'
          ? (op.hDots - decoded.heightDots / ky) / 2
          : 0
      const ux = Math.floor((sx - op.xDots - offsetX) * kx)
      const uy = Math.floor((sy - op.yDots - offsetY) * ky)
      if (ux < 0 || uy < 0 || ux >= decoded.widthDots || uy >= decoded.heightDots) return false
      return decoded.bits[uy * decoded.widthDots + ux] === 1
    })
    return
  }

  if (op.op === 'text') {
    raster.stats.textSlots += 1
    if (!options.outlineText) return
    /* 排错用：把"这一行占多宽、基线在哪"画成空心框（字形像素不在这里） */
    const x = op.xDots
    const y = op.yDots
    const w = Math.max(1, op.advanceDots)
    const h = Math.max(1, op.ascentDots)
    paint({ xDots: x, yDots: y, wDots: w, hDots: h }, (sx, sy) => {
      const dx = sx - x
      const dy = sy - y
      return dx === 0 || dy === 0 || dx === w - 1 || dy === h - 1
    })
  }
}

/** 把一个页面的 IR 画成 1-bit 位图 */
export function rasterizePage(page: PageRenderTree, options: RasterOptions = {}): Raster {
  const raster: Raster = {
    widthDots: page.widthDots,
    heightDots: page.heightDots,
    bits: new Uint8Array(page.widthDots * page.heightDots),
    stats: { ink: 0, textSlots: 0, imagePlaceholders: 0 },
  }
  for (const op of page.ops) rasterizeOp(raster, op, options)
  return raster
}

/**
 * 把位图画成 **ASCII**（`#` = 墨、`.` = 纸）。
 *
 * ⚠️ 这是这一层最实用的一个输出：**能在断言失败时直接看见**哪一块多了 / 少了墨
 * （像素级 diff 的数字说不出这个）。行距可以 `step` 抽稀，方便大页面。
 */
export function rasterToAscii(raster: Raster, step = 1): string {
  const lines: string[] = []
  for (let y = 0; y < raster.heightDots; y += step) {
    let line = ''
    for (let x = 0; x < raster.widthDots; x += step) {
      line += inkAt(raster, x, y) ? '#' : '.'
    }
    lines.push(line)
  }
  return lines.join('\n')
}

/** 某一个矩形区域里的上墨点数（断言用：静区、圆角、纸外…） */
export function inkCountIn(
  raster: Raster,
  box: { xDots: number; yDots: number; wDots: number; hDots: number },
): number {
  let count = 0
  for (let y = box.yDots; y < box.yDots + box.hDots; y += 1) {
    for (let x = box.xDots; x < box.xDots + box.wDots; x += 1) {
      if (inkAt(raster, x, y)) count += 1
    }
  }
  return count
}
